// last modified by AI tools on 2026-09-30
import type { ComputedRef, InjectionKey } from 'vue';

export type ElectionBoardLinkOrigin = 'table' | 'multi' | 'single';
export type ElectionBoardView = 'map' | 'grid';
export type ElectionBoardViewMode = 'dashboard' | 'grid';
export type ElectionBoardTab = 'governor' | 'senate' | 'house' | 'by-state';

const ELECTION_BOARD_SOURCE = 'election-board';
const ELECTION_BOARD_TABS: readonly ElectionBoardTab[] = ['governor', 'senate', 'house', 'by-state'];

/** Active Election Board tab, provided by ElectionHub so outbound links can carry it */
export const ELECTION_BOARD_TAB_KEY: InjectionKey<ComputedRef<ElectionBoardTab>> = Symbol('electionBoardTab');

function firstQueryValue(value: unknown): string | undefined {
  if (Array.isArray(value)) return typeof value[0] === 'string' ? value[0] : undefined;
  return typeof value === 'string' ? value : undefined;
}

export function resolveElectionBoardViewMode(
  query: Record<string, unknown>,
  routeViewMode?: ElectionBoardViewMode,
): ElectionBoardViewMode {
  const entryView = firstQueryValue(query.eb_view);
  if (entryView === 'grid') return 'grid';
  if (entryView === 'map') return 'dashboard';
  return routeViewMode ?? 'dashboard';
}

export function parseElectionBoardTab(query: Record<string, unknown>): ElectionBoardTab | null {
  const tab = firstQueryValue(query.eb_tab);
  return ELECTION_BOARD_TABS.includes(tab as ElectionBoardTab) ? (tab as ElectionBoardTab) : null;
}

export function parseElectionBoardDetailsTarget(
  query: Record<string, unknown>,
): { productConid: number; candidateConid?: number } | null {
  const productConid = Number(firstQueryValue(query.product));
  if (!Number.isFinite(productConid) || productConid <= 0) return null;

  const candidateConid = Number(firstQueryValue(query.candidate));
  return Number.isFinite(candidateConid) && candidateConid > 0 ? { productConid, candidateConid } : { productConid };
}

const DETAILS_TARGET_PARAMS = ['product', 'candidate'] as const;

/** Query without the race/candidate modal deep-link params, or null when none are present */
export function stripElectionBoardDetailsParams<T extends Record<string, unknown>>(query: T): Partial<T> | null {
  if (!DETAILS_TARGET_PARAMS.some((key) => key in query)) return null;
  const cleaned: Partial<T> = { ...query };
  for (const key of DETAILS_TARGET_PARAMS) delete cleaned[key];
  return cleaned;
}

export function buildPredictionMarketsUrl(options: {
  productConid: number;
  exchange: string;
  origin: ElectionBoardLinkOrigin;
  view: ElectionBoardView;
  tab?: ElectionBoardTab;
  candidateConid?: number;
}): string {
  const params = new URLSearchParams({
    exchange: options.exchange,
    from: ELECTION_BOARD_SOURCE,
    eb_origin: options.origin,
    eb_view: options.view,
  });
  if (options.tab) params.set('eb_tab', options.tab);
  if (options.candidateConid) params.set('eb_candidate', String(options.candidateConid));

  return `tws://forecast_trader2/${options.productConid}/product-details/contracts?${params.toString()}`;
}

export function buildElectionBoardReturnUrl(
  query: Record<string, unknown>,
  productConid: string | number | undefined,
): string | null {
  if (firstQueryValue(query.from) !== ELECTION_BOARD_SOURCE) return null;

  const origin = firstQueryValue(query.eb_origin);
  if (origin !== 'table' && origin !== 'multi' && origin !== 'single') return null;

  const view = firstQueryValue(query.eb_view) === 'map' ? 'map' : 'grid';
  const returnView = origin === 'table' ? 'grid' : view;
  const baseUrl = 'tws://forecast_trader2/election-hub';
  const params = new URLSearchParams({ eb_view: returnView });
  const tab = parseElectionBoardTab(query);
  if (tab) params.set('eb_tab', tab);
  if (origin === 'table') return `${baseUrl}?${params.toString()}`;

  const product = Number(productConid);
  if (!Number.isFinite(product) || product <= 0) return `${baseUrl}?${params.toString()}`;

  params.set('product', String(product));
  if (origin === 'single') {
    const candidate = Number(firstQueryValue(query.eb_candidate));
    if (Number.isFinite(candidate) && candidate > 0) params.set('candidate', String(candidate));
  }

  return `${baseUrl}?${params.toString()}`;
}
